import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Altura aproximada do cabeçalho fixo, para a seção não ficar escondida atrás dele. */
const HEADER_OFFSET = 88;

/**
 * O React Router não reposiciona a página sozinho. Sem isto:
 *  - trocar de rota mantém o scroll onde estava, abrindo a página nova no meio;
 *  - links com âncora vindos de outra rota (ex.: /#calculadora a partir de
 *    /pontuacao) chegam na home sem rolar até a seção.
 */
export default function ScrollToHash() {
  // `key` muda a cada navegação, inclusive quando pathname e hash continuam os
  // mesmos. Sem ele, clicar no link já ativo (estando em /#calculadora, clicar
  // "Calculadora" de novo depois de rolar) não reexecuta o efeito, e o Link usa
  // History API em vez de navegação nativa de fragmento — nada rola.
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (!hash) {
      // Duas passadas: uma imediata e outra após o paint da rota nova, porque
      // o conteúdo que entra depois pode reposicionar o scroll.
      window.scrollTo(0, 0);
      const settle = requestAnimationFrame(() => window.scrollTo(0, 0));
      return () => cancelAnimationFrame(settle);
    }

    const id = decodeURIComponent(hash.slice(1));
    let frame;
    const deadline = Date.now() + 1000;

    // A seção pode ainda não estar pintada quando a rota muda; tenta até achar.
    const attempt = () => {
      const target = document.getElementById(id);
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top: Math.max(top, 0), left: 0, behavior: 'smooth' });
        return;
      }
      if (Date.now() < deadline) {
        frame = requestAnimationFrame(attempt);
      }
    };

    frame = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, key]);

  return null;
}
